<template>
  <form
    ref="form"
    class="multi-store-options multi-store-options--inline"
    :class="`multi-store-options--${namespace}`"
    :action="action"
    method="post"
  >
    <input
      name="_method"
      type="hidden"
      value="put"
    >

    <input
      name="return_to"
      type="hidden"
      :value="returnTo"
    >

    <div class="multi-store-options__country-container">
      <div class="multi-store-options__selected-country text-other-label-small">
        {{ displayLabel }}
        <icon-arrow-down />
      </div>

      <label
        class="form-group__label visually-hidden"
        :for="`${namespace}-country-selector`"
        v-text="$string('general.multi_store.options.country')"
      />

      <select
        :id="`${namespace}-country-selector`"
        v-model="currentCountryCode"
        class="multi-store-options__country text-other-label-small"
        name="country_code"
        @change.prevent="handleSelectChange"
      >
        <option
          v-for="(country) of countries"
          :key="country.code"
          :value="country.code"
          v-text="getCountryOption(country)"
        />
      </select>
    </div>

    <div
      v-if="languageSelector"
      class="multi-store-options__language-container"
    >
      <label
        class="form-group__label visually-hidden"
        :for="`${namespace}-language-selector`"
        v-text="$string('general.multi_store.options.language')"
      />

      <select
        :id="`${namespace}-language-selector`"
        v-model="currentLanguageCode"
        class="multi-store-options__language text-other-label-small"
        name="language_code"
        @change.prevent="handleSelectChange"
      >
        <option
          v-for="(language) of languages"
          :key="language?.code"
          :value="language?.code"
          v-text="getLanguageOption(language)"
        />
      </select>
    </div>

    <input
      v-else
      name="language_code"
      type="hidden"
      :value="currentLanguageCode"
    >
  </form>
</template>

<script>

/**
 * Vue: Multi-store options inline (multi-store-options)
 * -----------------------------------------------------------------------------
 * Localization form selects with options.
 * - For use inline (not part of modal).
 * - Select names have to those names as they're set by Shopify.
 *
 * @param {Boolean} [languageSelector] - Display language selector.
 * @param {String} namespace - Namespace of component.
 *
 */
import { mapActions, mapState } from 'vuex'

import { multiStoreAction, multiStoreReturnTo, replaceMultiStoreStrings } from '~/helpers/multi-store'
import IconArrowDown from '~icons/directional-navigation/arrow/down.svg'

export default {
  name: 'MultiStoreOptionsInline',

  components: {
    IconArrowDown,
  },

  props: {
    languageSelector: {
      type: Boolean,
      default: true,
    },
    namespace: {
      type: String,
      default: '',
      required: true,
    },
  },

  data() {
    return {
      currentCountryCode: '',
      currentLanguageCode: '',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      current: (state) => state['multi-store'].current,
      selectedStore: (state) => state['multi-store'].selectedStore,
      suggestedStore: (state) => state['multi-store'].suggestedStore,
      supported: (state) => state['multi-store'].supported,
    }),

    /**
     * Compute form action based on current country code.
     * @returns {String}
     */
    action() {
      return multiStoreAction(this.active?.instance)
    },

    /**
     * Compute active value.
     * - This is selected if user has changed select value, or suggested if user
     *   hasn't updated anything yet.
     */
    active() {
      return this.selectedStore.country ? this.selectedStore : this.suggestedStore
    },

    /**
     * Compute countries array.
     * @returns {Array}
     */
    countries() {
      if (!Object.keys(this.supported).length) {
        return [this.current.country]
      }

      return Object.values(this.supported)
        .map((value) => value.country)
        .sort((a, b) => {
          return a.name.localeCompare(
            b.name,
            this.current.language.code.toLowerCase(),
          )
        })
    },

    /**
     * Display country code as label
     * @returns {string}
     */
    displayLabel() {
      const string = 'general.multi_store.format.country_inline'

      return this.$string(
        string,
        {
          replace: this.replaceStrings({
            country: this.active.country,
          }),
        },
      )
    },

    /**
     * Compute language choices based on current country.
     * @returns {Array}
     */
    languages() {
      return this.supported[this.currentCountryCode]?.languages?.length
        ? this.supported[this.currentCountryCode].languages
        : [this.current.language]
    },

    /**
     * Compute return to URL.
     * @returns {String}
     */
    returnTo() {
      return multiStoreReturnTo(true)
    },
  },

  watch: {

    /**
     * Watch current country code to update select width.
     * - Also tests to see if previously selected language exists in country.
     * @param {String} countryCode - New country code.
     */
    currentCountryCode(countryCode) {
      if (!countryCode) {
        return
      }

      const matchedLanguage = this.languages.find((language) => {
        return language?.code?.toUpperCase() === this.currentLanguageCode?.toUpperCase()
      })

      if (!matchedLanguage) {
        this.currentLanguageCode = this.languages[0]?.code?.toUpperCase()
      }

      if (this.supported[countryCode]) {
        this.setSelectedStore({
          country: this.supported[countryCode].country,
          instance: this.supported[countryCode].instance,
          language: matchedLanguage || this.languages[0],
        })
      }
    },

    /**
     * Watch current language code to update select width.
     * @param {String} languageCode - New language code.
     */
    currentLanguageCode(languageCode) {
      const matchedLanguage = this.languages.find((language) => {
        return language?.code?.toUpperCase() === languageCode?.toUpperCase()
      })

      if (this.supported[this.currentCountryCode]) {
        this.setSelectedStore({
          country: this.supported[this.currentCountryCode].country,
          instance: this.supported[this.currentCountryCode].instance,
          language: matchedLanguage || this.languages[0],
        })
      }
    },

    /**
     * Watch suggested store to update default selection.
     */
    suggestedStore() {
      this.setCurrentValues()
    },
  },

  mounted() {
    this.setCurrentValues()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      setPreference: 'multi-store/setPreference',
      setSelectedStore: 'multi-store/setSelectedStore',
    }),

    /**
     * Set current values.
     */
    setCurrentValues() {
      if (this.currentCountryCode || this.currentLanguageCode) {
        return
      }

      /**
       * Inline options should show current store.
       */
      this.currentCountryCode =
        this.current.country?.code ||
        cnvs.store.country

      this.currentLanguageCode =
        this.current.language?.code.toUpperCase() ||
        cnvs.store.language.toUpperCase()

    },

    /**
     * Get country option.
     * @param {Object} country - Current country.
     * @returns {String}
     */
    getCountryOption(country) {
      const string = 'general.multi_store.format.country'

      return this.$string(
        string,
        { replace: this.replaceStrings({ country }) },
      )
    },

    /**
     * Get language option.
     * @param {Object} language - Current language.
     * @returns {String}
     */
    getLanguageOption(language) {
      return this.$string(
        'general.multi_store.format.language',
        { replace: this.replaceStrings({ language }) },
      )
    },

    /**
     * Replace text shortcodes.
     * @returns {Object}
     */
    replaceStrings({ country, language } = {}) {
      return replaceMultiStoreStrings(country, language)
    },

    /**
     * Handle select change.
     */
    async handleSelectChange() {

      /**
       * Wait for watch processes to update language if it doesn't exist in the
       * new country choice.
       */
      await this.$nextTick()

      const form = this.$refs.form
      const formData = new FormData(form)

      const preferences = {
        country: formData.get('country_code'),
        language: formData.get('language_code'),
      }

      if (cnvs.storybook) {
        window.console.log('Submit form', preferences)
        return
      }

      this.setPreference(preferences)
      localStorage.setItem('multi-store-dismissed', JSON.stringify(preferences))

      /**
       * Wait for preferences to be set and action/returnTo computed props to
       * finish calculating.
       */
      await this.$nextTick()
      form.submit()
    },
  },
}
</script>

<style lang="scss">
@import './multi-store-options';
@import './multi-store-options-inline';
</style>
